import RAPIER from '@dimforge/rapier3d-compat'
import type { ColliderDesc, RigidBody, World } from '@dimforge/rapier3d-compat'
import type { ColliderData } from '../assets/extract'
import { SIM_DT } from '../core/loop'
import type { Quat, Vec3 } from '../core/math'
import type { ShipDef } from '../data/schemas'

/** Recenter Rapier's origin when the player gets farther than this from it (D-013). */
export const RECENTER_DISTANCE = 5000

/** Collision membership bits (Rapier interaction groups: membership in the high 16 bits, filter in the low 16). */
export const GROUP_SHIPS = 0x0001
export const GROUP_STATIC = 0x0002
/** M2 loose debris: freed salvage parts (dynamic bodies that are not ships). */
export const GROUP_DEBRIS = 0x0004
/** Ships collide with ships, statics and debris; statics with ships and debris; debris with ships and statics (not with other debris). */
export const SHIP_COLLISION_GROUPS = (GROUP_SHIPS << 16) | (GROUP_SHIPS | GROUP_STATIC | GROUP_DEBRIS)
export const STATIC_COLLISION_GROUPS = (GROUP_STATIC << 16) | (GROUP_SHIPS | GROUP_DEBRIS)
export const DEBRIS_COLLISION_GROUPS = (GROUP_DEBRIS << 16) | (GROUP_SHIPS | GROUP_STATIC)
/** `colliderEid` values for colliders that are not ship entities (raycasts report them as `RayHit.eid`). */
export const EID_ASTEROID = -1
export const EID_DERELICT = -2
/** M2 station statics (their trimesh colliders). */
export const EID_STATION = -3
/** M2 wreck hulls and the parts still attached to them. */
export const EID_WRECK = -4
/** M2 loose debris (freed parts). */
export const EID_DEBRIS = -5

let rapierReady: Promise<void> | undefined

/** Loads Rapier's WASM once. Safe to call repeatedly. */
export function initRapier(): Promise<void> {
  rapierReady ??= RAPIER.init()
  return rapierReady
}

export interface Physics {
  world: World
  /** The float64 world position that Rapier's (0, 0, 0) represents. */
  origin: Vec3
  /** Dynamic bodies indexed by entity id. */
  bodies: (RigidBody | undefined)[]
  /** Collider handle → entity id (the negative `EID_*` values for non-ship colliders). Written when colliders are created or removed only. */
  colliderEid: Map<number, number>
  /** L-A2: when `stepWorld` runs Rapier's JS bookkeeping (see `stepWorld`). */
  bookkeeping: WorldBookkeeping
  /** D-1118: this world's reused wasm-side vectors and read buffer for the per-tick Rapier calls. */
  raw: PhysicsRaw
}

/** Rapier's wasm-side vector (the package does not export the type). */
type RawVector = ReturnType<typeof RAPIER.VectorOps.intoRaw>

/**
 * D-1118: reused arguments for the per-tick Rapier calls. Rapier's `addForce`/`addTorque`/`PhysicsPipeline.step`
 * wrappers build a fresh `RawVector` (a wasm allocation plus a `FinalizationRegistry` entry) per call and free it
 * after; the raw methods only borrow it, so one per world, refilled through its setters, does the same work. The
 * setters take the same float32 conversion `new RawVector(x, y, z)` does. `buf` takes the raw getters' output in
 * place of the wrappers' module buffer + target object (whose float stores boxed). Created after `initRapier`.
 */
export class PhysicsRaw {
  readonly gravity: RawVector = RAPIER.VectorOps.intoRaw({ x: 0, y: 0, z: 0 })
  readonly force: RawVector = RAPIER.VectorOps.intoRaw({ x: 0, y: 0, z: 0 })
  readonly torque: RawVector = RAPIER.VectorOps.intoRaw({ x: 0, y: 0, z: 0 })
  readonly buf = new Float32Array(16) // Rapier sizes its own scratch to 16: the largest getter write
}

/** L-A2: Rapier's post-step JS bookkeeping state for one world. */
export interface WorldBookkeeping {
  /** A create/remove method ran since the last bookkeeping pass. */
  dirty: boolean
  /** Bookkeeping passes run (tests count them). */
  runs: number
}

/** A ray hit in float64 world space (D-011). */
export interface RayHit {
  /** Entity hit, or one of the negative `EID_*` values. */
  eid: number
  /** Distance along the (unit) ray direction, metres. */
  toi: number
  x: number
  y: number
  z: number
  /** Surface normal at the hit. */
  nx: number
  ny: number
  nz: number
}

/**
 * A ray in float64 world space (D-011): origin (ox, oy, oz), unit direction (dx, dy, dz) and length `maxToi` (m).
 * Hot callers keep one as module scratch and fill it with `setRay` (D-014): a raycast taking seven float
 * arguments boxes each one per call (the raycaster is never inlined). A class, not a literal, so its float
 * fields keep their own unboxed representation (gate-A decision).
 */
export class Ray {
  ox = 0
  oy = 0
  oz = 0
  dx = 0
  dy = 0
  dz = -1
  maxToi = 0
}

export function createRay(): Ray {
  return new Ray()
}

/** Fills `out` and returns it. Small enough for V8 to inline at every caller. */
export function setRay(out: Ray, ox: number, oy: number, oz: number, dx: number, dy: number, dz: number, maxToi: number): Ray {
  out.ox = ox
  out.oy = oy
  out.oz = oz
  out.dx = dx
  out.dy = dy
  out.dz = dz
  out.maxToi = maxToi
  return out
}

/**
 * Casts `ray` (a float64 world origin along a unit direction up to `maxToi` metres), ignoring the body of
 * `excludeEid` (-1 = none). Writes the closest hit into `out` and returns true on a hit. G2 implements it.
 */
export type RaycastFn = (ray: Ray, excludeEid: number, out: RayHit) => boolean

const castScratch: Ray = createRay()

/** Casts through `raycast` with the ray as numbers (tests and cold paths; hot paths keep their own `Ray`). */
export function castRay(
  raycast: RaycastFn,
  ox: number,
  oy: number,
  oz: number,
  dx: number,
  dy: number,
  dz: number,
  maxToi: number,
  excludeEid: number,
  out: RayHit,
): boolean {
  return raycast(setRay(castScratch, ox, oy, oz, dx, dy, dz, maxToi), excludeEid, out)
}

export function createRayHit(): RayHit {
  return { eid: -1, toi: 0, x: 0, y: 0, z: 0, nx: 0, ny: 0, nz: 0 }
}

export function createPhysics(): Physics {
  const world = new RAPIER.World({ x: 0, y: 0, z: 0 })
  world.timestep = SIM_DT
  const bookkeeping: WorldBookkeeping = { dirty: true, runs: 0 }
  trackWorldChanges(world, bookkeeping)
  return { world, origin: { x: 0, y: 0, z: 0 }, bodies: [], colliderEid: new Map(), bookkeeping, raw: new PhysicsRaw() }
}

/**
 * L-A2: every `World` method that adds or removes bodies, colliders, joints, soft bodies or their pieces.
 * Rapier 0.21's `World.step` ends with `mapNewSoftBodies()`, which walks every body, collider and joint to wrap
 * what the engine created on its own and drop what it removed. Outside soft bodies the engine only changes those
 * sets when one of these runs, so the walk is needed only after one of them (or while soft bodies exist, since
 * tears happen inside the step). `physics.test.ts` pins this list and the shape of `World.step`, so a Rapier
 * upgrade that adds a mutator or changes the step fails a test instead of silently skipping bookkeeping.
 */
export const WORLD_MUTATORS = [
  'createRigidBody',
  'createCollider',
  'createSoftBody',
  'createDeformableCollider',
  'addSoftBodyCluster',
  'removeSoftBodyCluster',
  'tearSoftBody',
  'cutSoftBody',
  'createImpulseJoint',
  'createMultibodyJoint',
  'removeRigidBody',
  'removeSoftBody',
  'removeCollider',
  'removeImpulseJoint',
  'removeMultibodyJoint',
] as const satisfies readonly (keyof World)[]

/** Wraps `world`'s mutators once (own properties on this instance) so each call marks `state` dirty. */
function trackWorldChanges(world: World, state: WorldBookkeeping): void {
  const target = world as unknown as Record<string, (...args: unknown[]) => unknown>
  for (const name of WORLD_MUTATORS) {
    const original = target[name]
    target[name] = function (this: unknown, ...args: unknown[]): unknown {
      state.dirty = true
      return original.apply(this, args)
    }
  }
}

/**
 * Advances `p.world` one step: exactly `World.step()` (no event queue, no hooks), except that the
 * `mapNewSoftBodies` bookkeeping runs only after a create/remove or while soft bodies exist (L-A2), and the
 * gravity goes in through the world's reused `RawVector` (`PhysicsRaw`) instead of a fresh one per step: the
 * pipeline's no-event branch, `raw.step(intoRaw(gravity), …raws)`. The JS side allocates nothing; the engine's own
 * `performance.now()` calls (~34 per step, profiler on or off) box their results inside the wasm glue.
 */
export function stepWorld(p: Physics): void {
  const w = p.world
  const g = p.raw.gravity
  g.x = w.gravity.x
  g.y = w.gravity.y
  g.z = w.gravity.z
  w.physicsPipeline.raw.step(
    g,
    w.integrationParameters.raw,
    w.islands.raw,
    w.broadPhase.raw,
    w.narrowPhase.raw,
    w.bodies.raw,
    w.colliders.raw,
    w.softBodies.raw,
    w.impulseJoints.raw,
    w.multibodyJoints.raw,
    w.ccdSolver.raw,
  )
  const bk = p.bookkeeping
  if (bk.dirty || w.softBodies.len() > 0) {
    bk.dirty = false
    bk.runs++
    w.mapNewSoftBodies()
  }
}

const ZERO = { x: 0, y: 0, z: 0 }
const IDENTITY = { x: 0, y: 0, z: 0, w: 1 }

/**
 * Dynamic ship body. Mass and inertia come from the def exactly; colliders have zero density (D-021).
 * Shape: one convex hull per `COL_` node of `collider`, its ball, or `ball(def.colliderRadius)` when absent.
 * Every collider is mapped to `eid` in `p.colliderEid`.
 */
export function createShipBody(
  p: Physics,
  eid: number,
  def: ShipDef,
  x: number,
  y: number,
  z: number,
  collider?: ColliderData | null,
): RigidBody {
  const shapes: ColliderDesc[] = []
  if (collider?.kind === 'convexHulls') {
    for (let i = 0; i < collider.hulls.length; i++) {
      const desc = RAPIER.ColliderDesc.convexHull(collider.hulls[i])
      if (!desc) throw new Error(`Ship ${def.id}: convex hull ${i} could not be built (null)`)
      shapes.push(desc)
    }
  } else if (collider?.kind === 'trimesh') {
    throw new Error(`Ship ${def.id}: trimesh colliders are for static bodies only`)
  } else {
    shapes.push(RAPIER.ColliderDesc.ball(collider?.kind === 'ball' ? collider.radius : def.colliderRadius))
  }

  const inertia = def.mass * def.inertiaScale
  const desc = RAPIER.RigidBodyDesc.dynamic()
    .setTranslation(x - p.origin.x, y - p.origin.y, z - p.origin.z)
    .setAdditionalMassProperties(def.mass, ZERO, { x: inertia, y: inertia, z: inertia }, IDENTITY)
  const body = p.world.createRigidBody(desc)
  for (let i = 0; i < shapes.length; i++) {
    let handle: number
    try {
      // Rapier 0.21 only computes a hull here (ColliderDesc.convexHull never returns null); a degenerate one throws.
      handle = p.world.createCollider(shapes[i].setDensity(0).setCollisionGroups(SHIP_COLLISION_GROUPS), body).handle
    } catch (err) {
      p.bodies[eid] = body
      removeShipBody(p, eid)
      throw new Error(`Ship ${def.id}: convex hull ${i} could not be built (${String(err)})`, { cause: err })
    }
    p.colliderEid.set(handle, eid)
  }
  // Rapier otherwise reports mass 0 until the next world step.
  body.recomputeMassPropertiesFromColliders()
  p.bodies[eid] = body
  return body
}

/** Removes ship `eid`'s body, its colliders and their collider-map entries. No-op when it has none. */
export function removeShipBody(p: Physics, eid: number): void {
  const body = p.bodies[eid]
  if (!body) return
  const n = body.numColliders()
  for (let i = 0; i < n; i++) p.colliderEid.delete(body.collider(i).handle)
  p.world.removeRigidBody(body)
  p.bodies[eid] = undefined
}

/** Static sphere collider (reference rocks in M0), mapped to EID_ASTEROID. */
export function createStaticBall(p: Physics, x: number, y: number, z: number, radius: number): RigidBody {
  const body = p.world.createRigidBody(
    RAPIER.RigidBodyDesc.fixed().setTranslation(x - p.origin.x, y - p.origin.y, z - p.origin.z),
  )
  const c = p.world.createCollider(RAPIER.ColliderDesc.ball(radius).setCollisionGroups(STATIC_COLLISION_GROUPS), body)
  p.colliderEid.set(c.handle, EID_ASTEROID)
  return body
}

const tv = { x: 0, y: 0, z: 0 }

/**
 * Reads a body into float64 world position plus rotation and velocities. Allocation-free (D-1118): the raw getters
 * fill the world's `PhysicsRaw.buf` directly, the same float32 values `translation()`/`rotation()`/`linvel()`/
 * `angvel()` copy out of Rapier's module buffer, without their target objects (whose float stores boxed).
 */
export function readBody(p: Physics, body: RigidBody, pos: Vec3, rot: Quat, vel: Vec3, angVel: Vec3): void {
  const set = p.world.bodies.raw
  const h = body.handle
  const b = p.raw.buf
  set.rbTranslation(h, b)
  pos.x = p.origin.x + b[0]
  pos.y = p.origin.y + b[1]
  pos.z = p.origin.z + b[2]
  set.rbRotation(h, b)
  rot.x = b[0]
  rot.y = b[1]
  rot.z = b[2]
  rot.w = b[3]
  set.rbLinvel(h, b)
  vel.x = b[0]
  vel.y = b[1]
  vel.z = b[2]
  set.rbAngvel(h, b)
  angVel.x = b[0]
  angVel.y = b[1]
  angVel.z = b[2]
}

/**
 * Replaces a body's user force and torque: exactly `resetForces(true)`, `resetTorques(true)`, `addForce(force,
 * true)`, `addTorque(torque, true)`, with the two vectors passed through the world's reused `RawVector`s instead
 * of a fresh one (plus finalizer) per call (D-1118). Allocation-free on the JS side.
 */
export function setBodyForces(p: Physics, body: RigidBody, force: Vec3, torque: Vec3): void {
  const set = p.world.bodies.raw
  const h = body.handle
  const f = p.raw.force
  const t = p.raw.torque
  set.rbResetForces(h, true)
  set.rbResetTorques(h, true)
  f.x = force.x
  f.y = force.y
  f.z = force.z
  set.rbAddForce(h, f, true)
  t.x = torque.x
  t.y = torque.y
  t.z = torque.z
  set.rbAddTorque(h, t, true)
}

const shift = { x: 0, y: 0, z: 0 }

/** Moves Rapier's origin to (nx, ny, nz) and shifts every body so float64 positions don't change. */
export function recenter(p: Physics, nx: number, ny: number, nz: number): void {
  const dx = p.origin.x - nx
  const dy = p.origin.y - ny
  const dz = p.origin.z - nz
  // ponytail: closure allocated per recenter; recenters are rare (every 5 km of travel).
  p.world.forEachRigidBody((body) => {
    body.translation(tv)
    shift.x = tv.x + dx
    shift.y = tv.y + dy
    shift.z = tv.z + dz
    body.setTranslation(shift, false)
  })
  p.origin.x = nx
  p.origin.y = ny
  p.origin.z = nz
}

/** Recentres on (x, y, z) if it is farther than RECENTER_DISTANCE from the current origin. */
export function recenterIfFar(p: Physics, x: number, y: number, z: number): boolean {
  const dx = x - p.origin.x
  const dy = y - p.origin.y
  const dz = z - p.origin.z
  if (dx * dx + dy * dy + dz * dz <= RECENTER_DISTANCE * RECENTER_DISTANCE) return false
  recenter(p, x, y, z)
  return true
}
